'use client';

import React from 'react';
import { AXES, Axis, AppConfig, Photo, finalValues } from '@/lib/types';

interface PhotoCardProps {
  photo: Photo;
  config: AppConfig;
  onCorrect: (photo: Photo, values: Record<Axis, string>) => void;
  onRetry: (photo: Photo) => void;
  onZoom: (photo: Photo) => void;
}

export function PhotoCard({ photo, config, onCorrect, onRetry, onZoom }: PhotoCardProps) {
  const values = finalValues(photo);
  const reviewed = photo.corrected_at !== null;
  const wasWrong =
    reviewed && AXES.some((a) => photo[`c_${a}`] !== photo[a]);
  const fileName = photo.path.split('/').pop();

  const border = photo.status === 'error'
    ? 'border-red-300'
    : wasWrong
      ? 'border-orange-300'
      : reviewed
        ? 'border-green-300'
        : 'border-gray-200';

  return (
    <div className={`bg-white rounded-lg border-2 ${border} overflow-hidden flex flex-col`}>
      <div className="relative aspect-[4/3] bg-gray-100">
        {photo.preview ? (
          <img
            src={`/api/preview/${photo.preview}`}
            alt={fileName}
            loading="lazy"
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => onZoom(photo)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            {photo.status === 'classifying' ? 'classifying…' : 'no preview yet'}
          </div>
        )}
        {reviewed && (
          <span
            className={`absolute top-2 right-2 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white ${
              wasWrong ? 'bg-orange-500' : 'bg-green-600'
            }`}
          >
            {wasWrong ? 'corrected — trains model' : 'confirmed'}
          </span>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 text-sm">
        <div className="font-mono text-xs text-gray-500 truncate" title={photo.path}>
          {fileName}
        </div>
        {photo.status === 'error' ? (
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs text-red-600 break-all">{photo.error}</span>
            <button
              onClick={() => onRetry(photo)}
              className="shrink-0 text-xs px-2 py-1 rounded text-white"
              style={{ backgroundColor: 'rgb(51, 102, 204)' }}
            >
              Retry
            </button>
          </div>
        ) : photo.status === 'done' ? (
          <>
            <div className="flex flex-wrap gap-1">
              {config.axes.product.map((v) => {
                const selectedList = values.product.split(' + ');
                const selected = selectedList.includes(v);
                return (
                  <button
                    key={v}
                    title="product — tap to toggle"
                    onClick={() => {
                      const next = selected
                        ? selectedList.filter((p) => p !== v)
                        : [...selectedList, v];
                      if (!next.filter(Boolean).length) return;
                      onCorrect(photo, {
                        ...values,
                        product: config.axes.product.filter((p) => next.includes(p)).join(' + '),
                      });
                    }}
                    className={`px-1.5 py-0.5 rounded text-[11px] border ${
                      selected
                        ? 'text-white border-transparent'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    style={selected ? { backgroundColor: 'rgb(51, 102, 204)' } : undefined}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {AXES.filter((a) => a !== 'product').map((a) => (
                <select
                  key={a}
                  value={values[a]}
                  title={a}
                  onChange={(e) => onCorrect(photo, { ...values, [a]: e.target.value })}
                  className={`border rounded px-1.5 py-1 text-xs bg-white ${
                    reviewed && photo[`c_${a}`] !== photo[a]
                      ? 'border-orange-400 text-orange-700'
                      : 'border-gray-300'
                  }`}
                >
                  {config.axes[a].map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              ))}
            </div>
            {!reviewed && (
              <button
                onClick={() => onCorrect(photo, values)}
                className="w-full text-xs py-1.5 rounded border border-green-500 text-green-700 hover:bg-green-50 transition-colors"
              >
                ✓ Looks right
              </button>
            )}
          </>
        ) : (
          <div className="text-xs text-gray-400">{photo.status}</div>
        )}
      </div>
    </div>
  );
}
